import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { IPaginationParams } from '../../services/types';
import { AppContext } from '../../context/AppContextProvider';
import TableComponent, { IColumnProps } from '../../components/TableComponent';
import ButtonActionsComponent from '../../components/ButtonActionsComponent';
import ButtonComponent from '../../components/ButtonComponent';
import { Add } from '@mui/icons-material';
import ConfirmDeleteModalComponent from '../../components/ConfirmDeleteModalComponent';
import {
  ISchoolBoardFilterParams,
  ISchoolBoardProps,
  ISchoolBoardSimpleProps,
} from '../../services/SchoolBoard/types';
import {
  useGetAllSchoolBoardsByLicenseAsync,
  useGetAllSchoolBoardsByLicenseCountAsync,
} from '../../services/SchoolBoard/hooks/useQuery';
import {
  useDeleteSchoolBoardAsync,
  useGetSchoolBoardByIdAsync,
} from '../../services/SchoolBoard/hooks/useMutation';
import ImageModalComponent from '../../components/ImageModalComponent';
import ImageSimpleColumnComponent from '../../components/ImageSimpleColumnComponent';
import dayjs from 'dayjs';
import SchoolBoardEditModal from 'pages/SchoolBoardPage/_components/SchoolBoardEditModal';

const SchoolBoardList = () => {
  const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);

  // Estados
  const [filter, setFilter] = useState<ISchoolBoardFilterParams>({
    search: '',
  });
  const [pagination, setPagination] = useState<IPaginationParams>({
    pageNumber: 1,
    pageSize: 10,
  });
  const [isShowImageModal, setIsShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [isVisibleEditModal, setIsVisibleEditModal] = useState(false);
  const [isVisibleDeleteModal, setIsVisibleDeleteModal] = useState(false);
  const [selectedToEdit, setSelectedToEdit] = useState<ISchoolBoardProps>();
  const [selectedToDelete, setSelectedToDelete] =
    useState<ISchoolBoardSimpleProps>();

  // Queries
  const {
    data: schoolBoards,
    refetch: refetchList,
    isLoading: isLoadingList,
  } = useGetAllSchoolBoardsByLicenseAsync(pagination, filter);

  const {
    data: totalCount,
    refetch: refetchCount,
    isLoading: isLoadingCount,
  } = useGetAllSchoolBoardsByLicenseCountAsync(pagination, filter);

  // Mutations
  const { mutateAsync: mutateGetById } = useGetSchoolBoardByIdAsync();
  const { mutateAsync: mutateDelete } = useDeleteSchoolBoardAsync();

  // Handlers Memoizados
  const refetchAll = useCallback(async () => {
    await Promise.all([refetchList(), refetchCount()]);
  }, [refetchList, refetchCount]);

  const handleEdit = async (id: number) => {
    setIsShowLoadingComponent(true);
    try {
      const data = await mutateGetById(id);
      setSelectedToEdit(data);
      setIsVisibleEditModal(true);
    } catch (error: any) {
      showSnackbar(error.message || 'Erro ao carregar dados', 'error');
    } finally {
      setIsShowLoadingComponent(false);
    }
  };

  const handleDeleteClick = (id: number) => {
    const item = schoolBoards?.find((i) => i.id === id);
    setSelectedToDelete(item);
    setIsVisibleDeleteModal(true);
  };

  const handleConfirmDeletion = async () => {
    if (!selectedToDelete?.id) return;

    setIsShowLoadingComponent(true);
    try {
      await mutateDelete(selectedToDelete.id);
      showSnackbar('Recado excluído com sucesso!', 'success');
      setIsVisibleDeleteModal(false);
      refetchAll();
    } catch (error: any) {
      showSnackbar(error.message, 'error');
    } finally {
      setIsShowLoadingComponent(false);
    }
  };

  const handleImageClick = (url: string) => {
    setSelectedImage(url);
    setIsShowImageModal(true);
  };

  const columns: IColumnProps[] = useMemo(
    () => [
      {
        id: 'name',
        label: 'Nome',
        width: 'auto',
      },
      {
        id: 'initialDate',
        label: 'Início em',
        width: 'auto',
        render: (val) => (
          <>{val ? dayjs(val).utc().format('DD/MM/YYYY') : '-'}</>
        ),
      },
      {
        id: 'validUntil',
        label: 'Validade',
        width: 'auto',
        render: (val) => (
          <>{val ? dayjs(val).utc().format('DD/MM/YYYY') : '-'}</>
        ),
      },
      {
        id: 'image',
        label: 'Imagem',
        width: 'auto',
        align: 'center',
        render: (url) => (
          <ImageSimpleColumnComponent
            url={String(url)}
            onClick={() => handleImageClick(String(url))}
          />
        ),
      },
      {
        id: 'id',
        label: 'Ações',
        align: 'center',
        width: '100px',
        render: (id) => (
          <ButtonActionsComponent
            isWithDefaultOptionsList
            handleDelete={() => handleDeleteClick(Number(id))}
            handleEdit={() => handleEdit(Number(id))}
          />
        ),
      },
    ],
    [schoolBoards],
  );

  return (
    <>
      <TableComponent
        columns={columns}
        rows={schoolBoards ?? []}
        sortByName="name"
        isLoading={isLoadingList || isLoadingCount}
        title="Mensageria"
        totalCount={totalCount ?? 0}
        onSubmitMainFilter={refetchAll}
        onChangeMainFilter={(val) =>
          setFilter((prev) => ({ ...prev, search: val as string }))
        }
        onChangePageOrRowsPerPage={setPagination}
        additionalTitleComponent={
          <ButtonComponent
            onClick={() => setIsVisibleEditModal(true)}
            startIcon={<Add />}
          >
            Novo Recado
          </ButtonComponent>
        }
      />

      {/* Modais */}
      <ConfirmDeleteModalComponent
        handleCancelDeletion={() => setIsVisibleDeleteModal(false)}
        handleConfirmDeletion={handleConfirmDeletion}
        isVisibleDeleteDialog={isVisibleDeleteModal}
        message={`Deseja excluir o recado "${selectedToDelete?.name}"?`}
        setIsVisibleDeleteDialog={setIsVisibleDeleteModal}
        title="Deletar Recado"
      />

      <ImageModalComponent
        isOpen={isShowImageModal}
        handleClose={() => setIsShowImageModal(false)}
        imageUrl={selectedImage}
        title="Visualização da Imagem"
        maxWidth="100%"
        maxHeight="100%"
      />

      {isVisibleEditModal && (
        <SchoolBoardEditModal
          isOpen={isVisibleEditModal}
          handleClose={() => {
            setIsVisibleEditModal(false);
            setSelectedToEdit(undefined);
          }}
          handleSaveForm={refetchAll}
          schoolBoardToEdit={selectedToEdit}
        />
      )}
    </>
  );
};

export default SchoolBoardList;
